import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Grid, IconButton, MenuItem, Select } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Add, Delete } from '@mui/icons-material';
import { FieldArray, getIn, useFormikContext } from 'formik';
import { TFunction } from 'i18next';
import * as yup from 'yup';
import { AccountData, PolicyTypes, ReschedulePolicyData, UpdateReschedulePolicyData } from '../../../api';
import { SettingsRow } from '../../formComponents/SettingsRow';
import { FormCheckbox } from '../../formComponents/FormCheckBox';
import { FormTextField } from '../../formComponents/FormTextField';

export type RescheduleFeeTierFormValues = {
  hours_before?: number;
  policy_type: PolicyTypes;
  value?: number;
};

export type ReschedulePolicyFormValues = {
  enabled: boolean;
  min_hours_before?: number;
  max_persons?: number;
  fee_tiers: RescheduleFeeTierFormValues[];
};

export type ReschedulePolicyOverrideFormValues = {
  reschedule_policy_override: boolean;
  reschedule_policy: ReschedulePolicyFormValues;
};

export const toReschedulePolicyFormValues = (policy?: ReschedulePolicyData): ReschedulePolicyFormValues => ({
  enabled: policy?.enabled ?? false,
  min_hours_before: policy?.min_hours_before ?? 0,
  max_persons: policy?.max_persons ?? undefined,
  fee_tiers: (policy?.fee_tiers ?? []).map((tier) => ({
    hours_before: tier.hours_before,
    policy_type: tier.policy_type,
    value: tier.value,
  })),
});

export const toReschedulePolicyPayload = (
  values: ReschedulePolicyOverrideFormValues,
): UpdateReschedulePolicyData | undefined =>
  values.reschedule_policy_override
    ? {
        enabled: values.reschedule_policy.enabled,
        min_hours_before: values.reschedule_policy.min_hours_before as number,
        max_persons: values.reschedule_policy.max_persons,
        fee_tiers: values.reschedule_policy.fee_tiers.map((tier) => ({
          hours_before: tier.hours_before as number,
          policy_type: tier.policy_type,
          value: tier.value as number,
        })),
      }
    : undefined;

export const reschedulePolicyOverrideSchema = (t: TFunction) =>
  yup.object().when('reschedule_policy_override', {
    is: true,
    then: (policySchema) =>
      policySchema.shape({
        min_hours_before: yup
          .number()
          .required(t('editOrderPage.yup.required'))
          .min(0, t('editOrderPage.yup.hasToBePoitive')),
        max_persons: yup.number().min(1, t('editOrderPage.yup.hasToBeMoreThan1')),
        fee_tiers: yup
          .array()
          .of(
            yup.object().shape({
              hours_before: yup
                .number()
                .required(t('editOrderPage.yup.required'))
                .min(1, t('editOrderPage.yup.hasToBeMoreThan1')),
              value: yup
                .number()
                .required(t('editOrderPage.yup.required'))
                .min(0, t('editOrderPage.yup.hasToBePoitive'))
                .when('policy_type', {
                  is: PolicyTypes.PERCENT,
                  then: (valueSchema) =>
                    valueSchema.max(100, t('accountSettings.reschedulePolicy.errors.percentRange')),
                }),
            }),
          )
          .test(
            'unique-hours-before',
            t('accountSettings.reschedulePolicy.errors.duplicate'),
            (tiers) => new Set((tiers ?? []).map((tier) => tier.hours_before)).size === (tiers ?? []).length,
          ),
      }),
  });

export const describeReschedulePolicyOverride = (values: ReschedulePolicyOverrideFormValues, t: TFunction) =>
  values.reschedule_policy_override
    ? t('editOrderPage.reschedulePolicy.custom')
    : t('editOrderPage.reschedulePolicy.inheritShort');

type ReschedulePolicyOverrideRowProps = {
  account: AccountData;
  disabled: boolean;
};

export const ReschedulePolicyOverrideRow = ({ account, disabled }: ReschedulePolicyOverrideRowProps) => {
  const { t } = useTranslation();
  const { values, errors, setFieldValue } = useFormikContext<ReschedulePolicyOverrideFormValues>();
  const policyDisabled = disabled || !values.reschedule_policy.enabled;
  const feeTiersError = getIn(errors, 'reschedule_policy.fee_tiers');

  return (
    <SettingsRow label={t('tabs.reschedulepolicy')}>
      <Grid container flexDirection="column" gap={2}>
        <Typography variant="body2" color="text.secondary">
          {t('editOrderPage.reschedulePolicy.subtitle')}
        </Typography>
        <Grid item md={6} xs={12}>
          <Select
            fullWidth
            value={values.reschedule_policy_override ? 'custom' : 'inherit'}
            onChange={(event) => setFieldValue('reschedule_policy_override', event.target.value === 'custom')}
            disabled={disabled}>
            <MenuItem value="inherit">
              {t('editOrderPage.reschedulePolicy.inherit', {
                value: account.reschedule_policy?.enabled
                  ? t('editOrderPage.reschedulePolicy.accountEnabled')
                  : t('editOrderPage.reschedulePolicy.accountDisabled'),
              })}
            </MenuItem>
            <MenuItem value="custom">{t('editOrderPage.reschedulePolicy.custom')}</MenuItem>
          </Select>
        </Grid>
        {values.reschedule_policy_override && (
          <>
            <FormCheckbox
              isFieldUpdate
              fieldName="reschedule_policy.enabled"
              label={t('accountSettings.reschedulePolicy.enabled')}
              disabled={disabled}
            />
            <FormTextField
              isFieldUpdate
              type="number"
              min={0}
              fieldName="reschedule_policy.min_hours_before"
              label={t('accountSettings.reschedulePolicy.minHoursBefore')}
              disabled={policyDisabled}
            />
            <FormTextField
              isFieldUpdate
              type="number"
              min={1}
              fieldName="reschedule_policy.max_persons"
              label={t('accountSettings.reschedulePolicy.maxPersons')}
              caption={t('editOrderPage.reschedulePolicy.maxPersonsCaption')}
              disabled={policyDisabled}
            />
            <FieldArray name="reschedule_policy.fee_tiers">
              {({ push, remove }) => (
                <Grid container flexDirection="column" gap={1}>
                  {values.reschedule_policy.fee_tiers.map((tier, index) => (
                    <Grid key={index} container alignItems="flex-start" gap={1} wrap="nowrap">
                      <FormTextField
                        isFieldUpdate
                        type="number"
                        min={1}
                        width={120}
                        fieldName={`reschedule_policy.fee_tiers.${index}.hours_before`}
                        label={t('accountSettings.reschedulePolicy.hoursBefore')}
                        disabled={policyDisabled}
                      />
                      <Select
                        value={tier.policy_type}
                        onChange={(event) =>
                          setFieldValue(`reschedule_policy.fee_tiers.${index}.policy_type`, event.target.value)
                        }
                        inputProps={{ 'aria-label': t('accountSettings.reschedulePolicy.type') }}
                        disabled={policyDisabled}
                        sx={{ minWidth: 120 }}>
                        <MenuItem value={PolicyTypes.PERCENT}>{t('accountSettings.reschedulePolicy.percent')}</MenuItem>
                        <MenuItem value={PolicyTypes.FIXED}>{t('accountSettings.reschedulePolicy.fixed')}</MenuItem>
                      </Select>
                      <FormTextField
                        isFieldUpdate
                        type="number"
                        min={0}
                        width={120}
                        fieldName={`reschedule_policy.fee_tiers.${index}.value`}
                        label={t('accountSettings.reschedulePolicy.value')}
                        disabled={policyDisabled}
                      />
                      <IconButton aria-label={t('delete')} disabled={policyDisabled} onClick={() => remove(index)}>
                        <Delete />
                      </IconButton>
                    </Grid>
                  ))}
                  {typeof feeTiersError === 'string' && (
                    <Typography variant="caption" color="error">
                      {feeTiersError}
                    </Typography>
                  )}
                  <Button
                    startIcon={<Add />}
                    disabled={policyDisabled}
                    onClick={() => push({ policy_type: PolicyTypes.PERCENT } as RescheduleFeeTierFormValues)}
                    sx={{ alignSelf: 'flex-start' }}>
                    {t('accountSettings.reschedulePolicy.addTier')}
                  </Button>
                </Grid>
              )}
            </FieldArray>
          </>
        )}
      </Grid>
    </SettingsRow>
  );
};
